/**
 * Shared runtime-narrowing utilities used across app pages.
 * No `as` assertions, no `any`, no `!` — all narrowing is done at runtime.
 */

export function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

export function readOptionalProp(obj: object, key: string): unknown {
  return key in obj ? Reflect.get(obj, key) : undefined
}

export function readStringProp(obj: object, key: string): string {
  const value = readOptionalProp(obj, key)
  return typeof value === 'string' ? value : ''
}

export type IntakeFormItem = { IntakeFormId?: string | null; Name?: string | null }

/**
 * `useIntakeFormTemplates` returns `unknown[]` (the generated intake-form contract is incomplete).
 * Narrow each entry to the `{ IntakeFormId, Name }` shape the visit-type form/select reads.
 */
export function normalizeIntakeForms(list: unknown[]): IntakeFormItem[] {
  return list.flatMap((item) => {
    if (item === null || typeof item !== 'object') return []
    const IntakeFormId = 'IntakeFormId' in item ? readStringProp(item, 'IntakeFormId') : ''
    const Name = 'Name' in item ? readStringProp(item, 'Name') : ''
    return [{ IntakeFormId, Name }]
  })
}

/** Shape consumed by the IntakeForms list view (management table). */
export type IntakeFormListItem = {
  IntakeFormId: string
  Name: string
  VisitTypeName: string
}

/**
 * `useIntakeForms` returns `unknown[]` (backend contract incomplete).
 * Narrow each entry to the fields the list table reads:
 * `{ IntakeFormId, Name, VisitTypeName }`.
 */
export function normalizeIntakeFormList(list: unknown[]): IntakeFormListItem[] {
  return list.flatMap((item) => {
    if (item === null || typeof item !== 'object') return []
    const IntakeFormId = 'IntakeFormId' in item ? readStringProp(item, 'IntakeFormId') : ''
    const Name = 'Name' in item ? readStringProp(item, 'Name') : ''
    const VisitTypeName = 'VisitTypeName' in item ? readStringProp(item, 'VisitTypeName') : ''
    return [{ IntakeFormId, Name, VisitTypeName }]
  })
}
